import React, { useCallback, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { Loader2, Maximize2, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DesgloseSemana } from "@/components/control/DesgloseSemana";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { calcEjercicios, GF_DEF, GP_DEF } from "@/lib/gastoFijo";
import { mesesDelAnio, semanasAnio } from "@/lib/reporteAnual";
import { ProyeccionTabla } from "@/components/control/ProyeccionTabla";
import { ComparativoAnual } from "@/components/control/ComparativoSemanal";
import { PantallaCompleta } from "@/components/control/PantallaCompleta";
import { ExportExcelButton } from "@/components/ExportExcelButton";

const card = "bg-white rounded-2xl border border-slate-200/70 shadow-sm";
const VISIBLES = 5;
const CMP_NO_ADMIN = 8;

function useDatosAnio(planta, structure, meses) {
  const q = (key, url, m, pick = (d) => d) => ({
    queryKey: [...key, planta, m.year, m.month],
    queryFn: async () => pick((await api.get(url, { params: { planta, structure, year: m.year, month: m.month } })).data),
  });
  const res = useQueries({
    queries: meses.flatMap((m) => [
      q([GP_DEF.mod, "ejercicios"], "/control-semanal/gestion-planta/ejercicios", m, (d) => d.items),
      q([GF_DEF.mod, "ejercicios"], "/control-semanal/gasto-fijo/ejercicios", m, (d) => d.items),
      q(["gestion-planta", "variables"], "/control-semanal/gestion-planta/variables", m),
      q(["proyeccion-mensual", "precios"], "/control-semanal/proyeccion-mensual/precios", m),
    ]),
  });
  if (res.some((r) => r.isError)) return { error: true };
  if (res.some((r) => !r.data)) return { loading: true };
  return {
    datos: meses.map((_, i) => {
      const [gp, gf, vars, pr] = res.slice(i * 4, i * 4 + 4).map((r) => r.data);
      return { gp: calcEjercicios(gp, GP_DEF), gf: calcEjercicios(gf, GF_DEF), vars: vars.valores, precios: pr.semanas };
    }),
  };
}

// Semáforo: Resultado real ≥ Resultado planeado (METAS) = verde; menor = rojo.
function semaforo(rows, semanas) {
  const res = rows.find((r) => r.k === "resultado");
  if (!res) return undefined;
  return Object.fromEntries(semanas.map((c) => [`${c.year}-${c.week}`, c.kg ? res.w(c) >= res.w({ ...c, plan: true, kg: c.planKg }) : null]));
}

function Toggle({ todas, setTodas, ocultas, n }) {
  return (
    <div className="flex justify-end">
      <Button variant="outline" size="sm" disabled={!ocultas && !todas} onClick={() => setTodas(!todas)} data-testid="ra-toggle-semanas"
        title={!ocultas && !todas ? "No hay semanas anteriores con venta real en este año" : undefined}>
        {todas ? <><ChevronsRight className="mr-1.5 h-4 w-4" />Mostrar últimas {n} semanas</> : <><ChevronsLeft className="mr-1.5 h-4 w-4" />Mostrar semanas anteriores ({ocultas})</>}
      </Button>
    </div>
  );
}

export function ReporteAnual({ planta, structure, cfg, modo = "corrida", semaforo: semaforoOn = false }) {
  const { user } = useAuth();
  const limitado = modo === "comparativo" && !user?.is_admin;
  const [year, setYear] = useState(cfg.semana_actual?.year ?? cfg.year);
  const [todas, setTodas] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [semSel, setSemSel] = useState("todas");
  const [fs, setFs] = useState(false);
  const cerrar = useCallback(() => setFs(false), []);
  const meses = useMemo(() => mesesDelAnio(year), [year]);
  const { datos, loading, error } = useDatosAnio(planta, structure, meses);
  const anio = useMemo(() => (datos ? semanasAnio(year, meses, datos) : null), [datos, year, meses]);
  const actual = cfg.semana_actual;
  const ultima = actual && year === actual.year ? actual.week : Infinity;
  const reales = (anio?.semanas || []).filter((c) => c.week <= ultima).map((c) => ({ ...c, real: true }));
  const filtro = semaforoOn && modo === "corrida";
  const elegida = filtro && semSel !== "todas" ? reales.find((c) => `${c.year}-${c.week}` === semSel) : null;
  const vista = elegida ? [elegida] : todas ? reales : reales.slice(-VISIBLES);
  const cmpSemanas = limitado ? reales.slice(-CMP_NO_ADMIN) : reales;
  const estados = semaforoOn && anio ? semaforo(anio.rows, modo === "comparativo" ? cmpSemanas : vista) : undefined;
  const actualKey = semaforoOn && actual ? `${actual.year}-${actual.week}` : undefined;

  let reportes = null;
  let body = <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>;
  if (error) body = <p className="text-sm text-red-600" data-testid="ra-error">No se pudo cargar la información del año.</p>;
  else if (anio) {
    const abrir = (c) => setDetalle(anio.semanas.find((x) => x.year === c.year && x.week === c.week) || null);
    reportes = modo === "comparativo"
      ? [{ key: "comparativo", label: "Comparativo semanal", node: <ComparativoAnual key={year} semanas={cmpSemanas} rows={anio.rows} onWeekClick={abrir} estados={estados} actual={actualKey} /> }]
      : [{ key: "corrida", label: "Corrida semanal", node: <ProyeccionTabla corrida filas={vista} rows={anio.rows} ym={{ year, month: 1 }} onWeekClick={abrir} estados={estados} actual={actualKey} vacio={`Aún no hay semanas con venta real capturada en ${year}.`} /> }];
    body = modo === "comparativo" ? reportes[0].node : (
      <div className="space-y-3">
        {!elegida && <Toggle todas={todas} setTodas={setTodas} ocultas={reales.length - vista.length} n={VISIBLES} />}
        {reportes[0].node}
        <p className="text-xs text-slate-500">Haz clic en el encabezado de una semana para ver su desglose.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid={`pm-planta-${planta}`}>
      <div className={`${card} p-4 flex flex-wrap items-center gap-3`}>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Año</span>
        <Select value={String(year)} disabled={limitado} onValueChange={(v) => { setYear(Number(v)); setTodas(false); setSemSel("todas"); }}>
          <SelectTrigger className="w-32" data-testid="ra-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[cfg.year - 2, cfg.year - 1, cfg.year, cfg.year + 1].map((y) => <SelectItem key={y} value={String(y)} data-testid={`ra-year-${y}`}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {filtro && anio && (
          <>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Semana</span>
            <Select value={elegida ? semSel : "todas"} onValueChange={setSemSel}>
              <SelectTrigger className="w-64" data-testid="ra-semana-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas" data-testid="ra-semana-todas">Todas las semanas</SelectItem>
                {[...reales].reverse().map((c) => <SelectItem key={c.week} value={`${c.year}-${c.week}`} data-testid={`ra-semana-${c.week}`}>Semana {c.week} · {c.sub}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
        <Button variant="outline" className="ml-auto" disabled={!reportes} onClick={() => setFs(true)} data-testid="pm-pantalla-completa"><Maximize2 className="mr-2 h-4 w-4" />Pantalla completa</Button>
        {reportes && <ExportExcelButton testid="pm-export" fileName={`${modo === "comparativo" ? "Comparativo" : "Corrida"}_Semanal_${planta}_${year}`} titulo={`${planta} · ${year}`} hojas={reportes.map((r) => ({ name: r.label, node: r.node }))} />}
      </div>
      {fs && reportes && <PantallaCompleta titulo={`${planta} · ${year}`} reportes={reportes} onClose={cerrar} />}
      <div className={`${card} p-5 space-y-4`}>
        <p className="text-center rounded-lg bg-[var(--gf-title)] py-1.5 text-lg font-bold text-white" data-testid="pm-planta-titulo">{planta}</p>
        {body}
        {semaforoOn && anio && (
          <p className="flex flex-wrap items-center gap-4 text-xs text-slate-500" data-testid="ra-semaforo-leyenda">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#15803d]" />Resultado real mayor o igual al planeado: cumple</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#b91c1c]" />Resultado real menor al planeado: no cumple</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#3B9AE1]" />Semana actual (en curso)</span>
            <span>Sin color: semana sin venta real</span>
          </p>
        )}
      </div>
      <DesgloseSemana semana={detalle} rows={anio?.rows || []} onClose={() => setDetalle(null)} cumple={semaforoOn && anio && detalle ? semaforo(anio.rows, [detalle])?.[`${detalle.year}-${detalle.week}`] : null} />
    </div>
  );
}
